import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { 
  BarChart3, AlertTriangle, TrendingDown, Calendar, 
  Filter, ChevronDown, ChevronUp, ArrowUpDown,
  Loader2, Package, AlertCircle, CheckCircle2, Search, X, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO, subDays, subMonths } from 'date-fns';
import { cs } from 'date-fns/locale';

// Skeleton loading components
const SkeletonCard = () => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
    <div className="h-8 bg-slate-200 rounded w-16 mb-2"></div>
    <div className="h-4 bg-slate-100 rounded w-24"></div>
  </div>
);

const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="px-4 py-3"><div className="h-5 bg-slate-200 rounded w-32"></div></td>
    <td className="px-4 py-3"><div className="h-5 bg-slate-100 rounded w-8"></div></td>
    <td className="px-4 py-3"><div className="h-5 bg-slate-100 rounded w-8"></div></td>
    <td className="px-4 py-3"><div className="h-8 bg-slate-200 rounded w-12"></div></td>
    <td className="px-4 py-3"><div className="h-4 bg-slate-100 rounded w-32"></div></td>
    <td className="px-4 py-3"><div className="h-5 bg-slate-100 rounded w-10"></div></td>
    <td className="px-4 py-3"><div className="h-5 bg-slate-100 rounded w-20"></div></td>
  </tr>
);

const SkeletonRedFlag = () => (
  <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 animate-pulse">
    <div className="flex items-start gap-2">
      <div className="w-4 h-4 bg-slate-200 rounded mt-0.5"></div>
      <div className="flex-1">
        <div className="h-4 bg-slate-200 rounded w-24 mb-2"></div>
        <div className="h-3 bg-slate-100 rounded w-40"></div>
      </div>
      <div className="h-5 bg-slate-200 rounded w-12"></div>
    </div>
  </div>
);

// Score to visual indicator
const ScoreIndicator = ({ score }) => {
  let color = 'bg-gray-200';
  let textColor = 'text-gray-600';
  
  if (score >= 2.7) {
    color = 'bg-green-500';
    textColor = 'text-white';
  } else if (score >= 2.3) {
    color = 'bg-green-300';
    textColor = 'text-green-800';
  } else if (score >= 2.0) {
    color = 'bg-amber-300';
    textColor = 'text-amber-800';
  } else if (score >= 1.5) {
    color = 'bg-orange-400';
    textColor = 'text-white';
  } else if (score > 0) {
    color = 'bg-red-500';
    textColor = 'text-white';
  }
  
  return (
    <span className={`inline-flex items-center justify-center w-12 h-8 rounded font-bold ${color} ${textColor}`}>
      {score.toFixed(2)}
    </span>
  );
};

// Rating bar component
const RatingBar = ({ a, b, c, showLabels = true }) => {
  const total = a + b + c;
  if (total === 0) return <span className="text-slate-400 text-sm">-</span>;
  
  const pctA = (a / total * 100).toFixed(0);
  const pctB = (b / total * 100).toFixed(0);
  const pctC = (c / total * 100).toFixed(0);
  
  return (
    <div className="flex flex-col gap-1">
      <div className="flex h-4 w-full rounded overflow-hidden">
        {a > 0 && (
          <div 
            className="bg-green-500 flex items-center justify-center text-[10px] font-medium text-white"
            style={{ width: `${pctA}%` }}
          >
            {pctA > 15 && `${pctA}%`}
          </div>
        )}
        {b > 0 && (
          <div 
            className="bg-amber-400 flex items-center justify-center text-[10px] font-medium text-amber-900"
            style={{ width: `${pctB}%` }}
          >
            {pctB > 15 && `${pctB}%`}
          </div>
        )}
        {c > 0 && (
          <div 
            className="bg-red-500 flex items-center justify-center text-[10px] font-medium text-white"
            style={{ width: `${pctC}%` }}
          >
            {pctC > 15 && `${pctC}%`}
          </div>
        )}
      </div>
      {showLabels && (
        <div className="flex gap-2 text-[10px] text-slate-500">
          <span>A: {a}</span>
          <span>B: {b}</span>
          <span>C: {c}</span>
        </div>
      )}
    </div>
  );
};

// Category detail row
const CategoryRow = ({ category, data }) => {
  const { A, B, C, percent_c, total } = data;
  const isWarning = percent_c > 30;
  
  return (
    <tr className={isWarning ? 'bg-red-50' : ''}>
      <td className="px-3 py-2 text-sm font-medium text-slate-700">
        {category}
        {isWarning && <AlertTriangle className="inline ml-1 w-3 h-3 text-red-500" />}
      </td>
      <td className="px-3 py-2">
        <RatingBar a={A} b={B} c={C} showLabels={false} />
      </td>
      <td className="px-3 py-2 text-center text-sm">{A}</td>
      <td className="px-3 py-2 text-center text-sm">{B}</td>
      <td className="px-3 py-2 text-center text-sm font-medium text-red-600">{C}</td>
      <td className="px-3 py-2 text-center text-sm">
        <span className={percent_c > 30 ? 'text-red-600 font-bold' : 'text-slate-600'}>
          {percent_c}%
        </span>
      </td>
    </tr>
  );
};

// Food detail dialog with trend chart
const FoodDetailDialog = ({ food, open, onClose, sensoryType }) => {
  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  
  useEffect(() => {
    if (food && open) {
      fetchTimeline();
    }
  }, [food, open]);
  
  const fetchTimeline = async () => {
    if (!food) return;
    setLoadingTimeline(true);
    try {
      const response = await api.get(`/sensory/analytics/food/${encodeURIComponent(food.food_name)}?sensory_type=${sensoryType}`);
      
      // Transform timeline data for chart
      const chartData = (response.data.timeline || []).map(t => {
        // Calculate average score for this test
        let totalA = 0, totalB = 0, totalC = 0;
        Object.values(t.ratings_summary || {}).forEach(r => {
          totalA += r.A || 0;
          totalB += r.B || 0;
          totalC += r.C || 0;
        });
        const total = totalA + totalB + totalC;
        const avgScore = total > 0 ? ((totalA * 3 + totalB * 2 + totalC * 1) / total) : 0;
        const percentC = total > 0 ? ((totalC / total) * 100) : 0;
        
        return {
          date: t.test_date,
          displayDate: format(parseISO(t.test_date), 'dd.MM', { locale: cs }),
          fullDate: format(parseISO(t.test_date), 'dd.MM.yyyy', { locale: cs }),
          score: Math.round(avgScore * 100) / 100,
          percentC: Math.round(percentC * 10) / 10,
          batch: t.batch,
          evaluators: t.evaluator_count
        };
      }).reverse(); // Oldest first for chart
      
      setTimeline(chartData);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setLoadingTimeline(false);
    }
  };
  
  if (!food) return null;
  
  const categories = Object.entries(food.ratings_by_category || {})
    .sort((a, b) => (b[1].percent_c || 0) - (a[1].percent_c || 0));
  
  // Calculate trend direction
  const getTrendIndicator = () => {
    if (timeline.length < 2) return null;
    const recent = timeline.slice(-3);
    const older = timeline.slice(0, Math.max(1, timeline.length - 3));
    
    const recentAvg = recent.reduce((s, t) => s + t.score, 0) / recent.length;
    const olderAvg = older.reduce((s, t) => s + t.score, 0) / older.length;
    
    const diff = recentAvg - olderAvg;
    if (Math.abs(diff) < 0.1) return { direction: 'stable', color: 'text-slate-500' };
    if (diff > 0) return { direction: 'up', color: 'text-green-500' };
    return { direction: 'down', color: 'text-red-500' };
  };
  
  const trend = getTrendIndicator();
  
  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
          <p className="font-medium text-slate-800">{data.fullDate}</p>
          <p className="text-sm text-slate-600">Šarže: {data.batch || '-'}</p>
          <p className="text-sm text-slate-600">Hodnotitelů: {data.evaluators}</p>
          <div className="mt-2 pt-2 border-t border-slate-100">
            <p className="text-sm">
              <span className="font-medium text-blue-600">Skóre: {data.score}</span>
            </p>
            <p className="text-sm">
              <span className={`font-medium ${data.percentC > 20 ? 'text-red-600' : 'text-slate-600'}`}>
                Podíl C: {data.percentC}%
              </span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            {food.food_name}
            {trend && (
              <span className={`ml-2 ${trend.color}`}>
                {trend.direction === 'up' && <TrendingUp className="w-4 h-4 inline" />}
                {trend.direction === 'down' && <TrendingDown className="w-4 h-4 inline" />}
                {trend.direction === 'stable' && <span className="text-xs">→</span>}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Summary stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-slate-700">{food.test_count}</div>
              <div className="text-xs text-slate-500">Testů</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-slate-700">{food.evaluation_count}</div>
              <div className="text-xs text-slate-500">Hodnocení</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <ScoreIndicator score={food.average_score} />
              <div className="text-xs text-slate-500 mt-1">Prům. skóre</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <div className={`text-2xl font-bold ${food.percent_c > 20 ? 'text-red-600' : 'text-slate-700'}`}>
                {food.percent_c}%
              </div>
              <div className="text-xs text-slate-500">Podíl C</div>
            </div>
          </div>
          
          {/* Trend chart */}
          {timeline.length >= 2 && (
            <div>
              <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Vývoj hodnocení v čase
              </h4>
              <div className="bg-slate-50 rounded-lg p-4">
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={timeline} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="displayDate" 
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickLine={{ stroke: '#cbd5e1' }}
                    />
                    <YAxis 
                      domain={[1, 3]} 
                      ticks={[1, 1.5, 2, 2.5, 3]}
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickLine={{ stroke: '#cbd5e1' }}
                      label={{ value: 'Skóre', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#64748b' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={2} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: 'Min', fontSize: 10, fill: '#f59e0b' }} />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, fill: '#2563eb' }}
                      name="Skóre"
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-6 mt-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-0.5 bg-blue-500 inline-block"></span> Skóre (A=3, B=2, C=1)
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-0.5 bg-amber-500 inline-block" style={{ borderStyle: 'dashed' }}></span> Minimální standard
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {loadingTimeline && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500 mr-2" />
              <span className="text-sm text-slate-500">Načítám historii...</span>
            </div>
          )}
          
          {timeline.length === 1 && !loadingTimeline && (
            <div className="text-center py-4 text-slate-500 text-sm bg-slate-50 rounded-lg">
              Pro zobrazení grafu je potřeba více testů
            </div>
          )}
          
          {/* Overall rating distribution */}
          <div>
            <h4 className="font-medium text-slate-700 mb-2">Celkové rozložení hodnocení</h4>
            <RatingBar 
              a={food.total_ratings.A} 
              b={food.total_ratings.B} 
              c={food.total_ratings.C} 
            />
          </div>
          
          {/* Category breakdown */}
          <div>
            <h4 className="font-medium text-slate-700 mb-2">Hodnocení podle kategorií</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600">Kategorie</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 w-32">Rozložení</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-green-600 w-12">A</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-amber-600 w-12">B</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-red-600 w-12">C</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-slate-600 w-16">% C</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {categories.map(([cat, data]) => (
                    <CategoryRow key={cat} category={cat} data={data} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Last test info */}
          {food.last_test_date && (
            <div className="text-sm text-slate-500">
              Poslední test: {format(parseISO(food.last_test_date), 'dd.MM.yyyy', { locale: cs })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Red flag card
const RedFlagCard = ({ flag }) => {
  const isOverall = flag.type === 'high_c_overall';
  
  return (
    <div className={`p-3 rounded-lg border ${isOverall ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className={`w-4 h-4 mt-0.5 ${isOverall ? 'text-red-500' : 'text-orange-500'}`} />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-800 truncate">{flag.food_name}</div>
          <div className="text-sm text-slate-600">{flag.message}</div>
        </div>
        <Badge variant={isOverall ? 'destructive' : 'secondary'} className="shrink-0">
          {flag.percent_c}% C
        </Badge>
      </div>
    </div>
  );
};

export default function SensoryAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ foods: [], red_flags: [], summary: {} });
  const [sensoryType, setSensoryType] = useState('hotovky');
  const [period, setPeriod] = useState('month');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [sortField, setSortField] = useState('evaluation_count');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedFood, setSelectedFood] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('sensory_type', sensoryType);
      
      if (dateFrom && dateTo) {
        params.append('date_from', format(dateFrom, 'yyyy-MM-dd'));
        params.append('date_to', format(dateTo, 'yyyy-MM-dd'));
      } else if (period) {
        params.append('period', period);
      }
      
      const response = await api.get(`/sensory/analytics?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Chyba při načítání dat');
    } finally {
      setLoading(false);
    }
  }, [sensoryType, period, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter and sort foods
  const filteredAndSortedFoods = [...data.foods]
    .filter(food => {
      if (!searchQuery.trim()) return true;
      return food.food_name.toLowerCase().includes(searchQuery.toLowerCase());
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortHeader = ({ field, children }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && (
          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
        {sortField !== field && <ArrowUpDown className="w-3 h-3 text-slate-300" />}
      </div>
    </th>
  );

  // Quick period buttons
  const periodOptions = [
    { value: 'week', label: 'Týden' },
    { value: 'month', label: 'Měsíc' },
    { value: 'quarter', label: 'Kvartál' },
    { value: 'year', label: 'Rok' },
  ];

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="sensory-analytics-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-blue-600" />
              Hodnocení senzorik
            </h1>
            <p className="text-slate-500 mt-1">
              Analýza hodnocení jídel podle senzorických zkoušek
            </p>
          </div>
          
          {/* Type selector */}
          <div className="flex items-center gap-2">
            <Button
              variant={sensoryType === 'hotovky' ? 'default' : 'outline'}
              onClick={() => setSensoryType('hotovky')}
              data-testid="type-hotovky"
            >
              Hotovky
            </Button>
            <Button
              variant={sensoryType === 'krabicky' ? 'default' : 'outline'}
              onClick={() => setSensoryType('krabicky')}
              data-testid="type-krabicky"
            >
              Krabičky
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Period quick select */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-600">Období:</span>
              {periodOptions.map(opt => (
                <Button
                  key={opt.value}
                  size="sm"
                  variant={period === opt.value && !dateFrom ? 'default' : 'outline'}
                  onClick={() => {
                    setPeriod(opt.value);
                    setDateFrom(null);
                    setDateTo(null);
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            
            {/* Custom date range toggle */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowFilters(!showFilters)}
              className="ml-auto"
            >
              <Filter className="w-4 h-4 mr-1" />
              Vlastní období
            </Button>
          </div>
          
          {/* Custom date range */}
          {showFilters && (
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Od:</span>
                <DatePicker
                  date={dateFrom}
                  onDateChange={(date) => {
                    setDateFrom(date);
                    setPeriod(null);
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Do:</span>
                <DatePicker
                  date={dateTo}
                  onDateChange={(date) => {
                    setDateTo(date);
                    setPeriod(null);
                  }}
                />
              </div>
              {dateFrom && dateTo && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDateFrom(null);
                    setDateTo(null);
                    setPeriod('month');
                  }}
                >
                  Zrušit
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : (
            <>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="text-3xl font-bold text-slate-800">{data.summary.total_tests || 0}</div>
                <div className="text-sm text-slate-500">Celkem testů</div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="text-3xl font-bold text-slate-800">{data.summary.total_evaluations || 0}</div>
                <div className="text-sm text-slate-500">Celkem hodnocení</div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="text-3xl font-bold text-slate-800">{data.summary.unique_foods || 0}</div>
                <div className="text-sm text-slate-500">Unikátních jídel</div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="text-3xl font-bold text-red-600">{data.red_flags?.length || 0}</div>
                <div className="text-sm text-slate-500">Upozornění</div>
              </div>
            </>
          )}
        </div>

        {/* Red flags section */}
        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-slate-200 rounded animate-pulse"></div>
              <div className="h-5 bg-slate-200 rounded w-48 animate-pulse"></div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              <SkeletonRedFlag />
              <SkeletonRedFlag />
              <SkeletonRedFlag />
            </div>
          </div>
        ) : data.red_flags?.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Upozornění na problematická jídla
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.red_flags.slice(0, 6).map((flag, idx) => (
                <RedFlagCard key={idx} flag={flag} />
              ))}
            </div>
            {data.red_flags.length > 6 && (
              <div className="text-sm text-slate-500 mt-2">
                a dalších {data.red_flags.length - 6} upozornění...
              </div>
            )}
          </div>
        )}

        {/* Foods table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-lg font-semibold text-slate-800">
              Přehled jídel
            </h2>
            {/* Search input */}
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Vyhledat jídlo..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-8"
                data-testid="food-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                >
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Jídlo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Testů</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Hodnocení</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Skóre</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Rozložení</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">% C</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Poslední test</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </tbody>
              </table>
            </div>
          ) : filteredAndSortedFoods.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              {searchQuery ? `Žádné jídlo neodpovídá hledání "${searchQuery}"` : 'Žádná data pro vybrané období'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="foods-table">
                <thead className="bg-slate-50">
                  <tr>
                    <SortHeader field="food_name">Jídlo</SortHeader>
                    <SortHeader field="test_count">Testů</SortHeader>
                    <SortHeader field="evaluation_count">Hodnocení</SortHeader>
                    <SortHeader field="average_score">Skóre</SortHeader>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">
                      Rozložení A/B/C
                    </th>
                    <SortHeader field="percent_c">% C</SortHeader>
                    <SortHeader field="last_test_date">Poslední test</SortHeader>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredAndSortedFoods.map((food, idx) => (
                    <tr 
                      key={idx} 
                      className={`hover:bg-slate-50 cursor-pointer ${food.percent_c > 20 ? 'bg-red-50' : ''}`}
                      onClick={() => setSelectedFood(food)}
                      data-testid={`food-row-${idx}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{food.food_name}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{food.test_count}</td>
                      <td className="px-4 py-3 text-slate-600">{food.evaluation_count}</td>
                      <td className="px-4 py-3">
                        <ScoreIndicator score={food.average_score} />
                      </td>
                      <td className="px-4 py-3 w-48">
                        <RatingBar 
                          a={food.total_ratings.A} 
                          b={food.total_ratings.B} 
                          c={food.total_ratings.C}
                          showLabels={false}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-medium ${food.percent_c > 20 ? 'text-red-600' : 'text-slate-600'}`}>
                          {food.percent_c}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-sm">
                        {food.last_test_date ? format(parseISO(food.last_test_date), 'dd.MM.yyyy', { locale: cs }) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Food detail dialog */}
        <FoodDetailDialog 
          food={selectedFood} 
          open={!!selectedFood} 
          onClose={() => setSelectedFood(null)}
          sensoryType={sensoryType}
        />
      </div>
    </Layout>
  );
}
